import {
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

import { Region } from '../../../../../../../api/theHive/redeProto/types';
import {
  Constants,
  REGIONS,
  UF_REGION,
} from '../../../../../../../constants/redeProto';
import { SectionCard } from '../../../components/SectionCard';
import { Note, ProgressFill, ProgressTrack } from '../../../styles';
import { formatInt, formatPct } from '../../../utils/format';
import { PoleCapacity, UfCapacity } from '../types';
import { occupancyColor } from '../utils';
import { CapacityMap } from './CapacityMap';
import { CapacityLayout, TableScroll } from './styles';

type SortKey = 'name' | 'uf' | 'occupancy' | 'availableSlots' | 'waitlist';

const COLUMNS: { key: SortKey; label: string; numeric: boolean }[] = [
  {
    key: 'name',
    label: Constants.inAccreditation.capacity.pole,
    numeric: false,
  },
  { key: 'uf', label: Constants.inAccreditation.capacity.uf, numeric: false },
  {
    key: 'occupancy',
    label: Constants.inAccreditation.capacity.occupancy,
    numeric: true,
  },
  {
    key: 'availableSlots',
    label: Constants.inAccreditation.capacity.slots,
    numeric: true,
  },
  {
    key: 'waitlist',
    label: Constants.inAccreditation.capacity.waitlist,
    numeric: true,
  },
];

interface CapacitySectionProps {
  poles: PoleCapacity[];
  ufs: UfCapacity[];
  needsNoticeSelection: boolean;
  truncated: boolean;
  loading: boolean;
}

export function CapacitySection({
  poles,
  ufs,
  needsNoticeSelection,
  truncated,
  loading,
}: CapacitySectionProps): JSX.Element {
  const [region, setRegion] = useState<Region | 'all'>('all');
  const [uf, setUf] = useState('all');
  const [sortKey, setSortKey] = useState<SortKey>('waitlist');
  const [descending, setDescending] = useState(true);

  const ufOptions = useMemo(() => {
    const scoped =
      region === 'all'
        ? poles
        : poles.filter((pole) => UF_REGION[pole.uf] === region);
    return Array.from(new Set(scoped.map((pole) => pole.uf))).sort();
  }, [poles, region]);

  const visiblePoles = useMemo(() => {
    const rows = poles.filter((pole) => {
      if (region !== 'all' && UF_REGION[pole.uf] !== region) return false;
      if (uf !== 'all' && pole.uf !== uf) return false;
      return true;
    });

    return rows.sort((a, b) => {
      const direction = descending ? -1 : 1;
      if (sortKey === 'name' || sortKey === 'uf') {
        return a[sortKey].localeCompare(b[sortKey]) * direction;
      }
      return (a[sortKey] - b[sortKey]) * direction;
    });
  }, [poles, region, uf, sortKey, descending]);

  const visibleUfs = useMemo(
    () =>
      ufs.filter((item) => {
        if (region !== 'all' && UF_REGION[item.uf] !== region) return false;
        if (uf !== 'all' && item.uf !== uf) return false;
        return true;
      }),
    [ufs, region, uf]
  );

  const handleSort = (key: SortKey): void => {
    if (key === sortKey) {
      setDescending((current) => !current);
      return;
    }
    setSortKey(key);
    // Texto começa em A–Z; número começa do maior, que é o que interessa ver.
    setDescending(key !== 'name' && key !== 'uf');
  };

  if (needsNoticeSelection) {
    return (
      <SectionCard
        title={Constants.inAccreditation.capacity.title}
        subtitle={Constants.inAccreditation.capacity.subtitle}
      >
        <Typography color="text.secondary" fontSize={14}>
          {Constants.inAccreditation.capacity.selectNotice}
        </Typography>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title={Constants.inAccreditation.capacity.title}
      subtitle={Constants.inAccreditation.capacity.subtitle}
      action={
        <Box display="flex" gap={1} flexWrap="wrap">
          <TextField
            select
            size="small"
            value={region}
            onChange={(event) => {
              setRegion(event.target.value as Region | 'all');
              setUf('all');
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">{Constants.geoFilters.allRegions}</MenuItem>
            {REGIONS.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            value={uf}
            onChange={(event) => setUf(event.target.value)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="all">{Constants.geoFilters.allUfs}</MenuItem>
            {ufOptions.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      }
    >
      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && poles.length === 0 && (
        <Typography color="text.secondary" fontSize={14}>
          {Constants.inAccreditation.capacity.empty}
        </Typography>
      )}

      {!loading && truncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {Constants.inAccreditation.capacity.partial}
        </Alert>
      )}

      {!loading && poles.length > 0 && (
        <>
          <CapacityLayout>
            <Box>
              <Typography fontWeight={600} fontSize={14} mb={1}>
                {Constants.inAccreditation.capacity.mapTitle}
              </Typography>
              <CapacityMap ufs={visibleUfs} />
            </Box>

            <Box>
              <Typography fontWeight={600} fontSize={14} mb={1}>
                {`${formatInt(visiblePoles.length)} ${
                  Constants.inAccreditation.capacity.polesCount
                }`}
              </Typography>
              <TableScroll>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {COLUMNS.map((column) => (
                        <TableCell
                          key={column.key}
                          align={column.numeric ? 'center' : 'left'}
                          sortDirection={
                            sortKey === column.key
                              ? (descending && 'desc') || 'asc'
                              : false
                          }
                        >
                          <TableSortLabel
                            active={sortKey === column.key}
                            direction={
                              sortKey === column.key && descending
                                ? 'desc'
                                : 'asc'
                            }
                            onClick={() => handleSort(column.key)}
                          >
                            {column.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visiblePoles.map((pole) => (
                      <TableRow key={pole.id}>
                        <TableCell>{pole.name}</TableCell>
                        <TableCell align="center">{pole.uf}</TableCell>
                        <TableCell align="center">
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            minWidth={120}
                          >
                            <ProgressTrack sx={{ minWidth: '80px' }}>
                              <ProgressFill
                                pct={pole.occupancy}
                                color={occupancyColor(pole.occupancy)}
                              />
                            </ProgressTrack>
                            <Typography fontSize={12} whiteSpace="nowrap">
                              {formatPct(pole.occupancy, 0)}
                            </Typography>
                          </Box>
                          <Typography fontSize={11} color="text.secondary">
                            {`${formatInt(pole.active)} de ${formatInt(
                              pole.limit
                            )}`}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {formatInt(pole.availableSlots)}
                        </TableCell>
                        <TableCell align="center">
                          {pole.waitlist > 0 ? (
                            <Typography fontWeight={600} fontSize={13}>
                              {formatInt(pole.waitlist)}
                            </Typography>
                          ) : (
                            <Typography color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableScroll>
            </Box>
          </CapacityLayout>

          <Note>{Constants.inAccreditation.capacity.mapSubtitle}</Note>
        </>
      )}
    </SectionCard>
  );
}
